import * as DV from "@impero-com/common/utils/dataView";
import {
    ExportableArray,
    exportArrayValue,
} from "@impero-com/common/utils/export";
import { assertNever } from "@impero-com/common/utils/obj";
import { range } from "lodash-es";

import {
    ControlOverviewListDto,
    MitigatingControl,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    ControlOverview,
    ControlProgramOverview,
} from "api/generated/model";
import { forwardToRisk, sanitizeWhiteSpaces } from "org/risks/content/utils";
import { RISK_EXTRACTOR_KEYS } from "org/risks/types";

import * as T from "../types";

const CONTROL_PROGRAM_NAME = "Control program name";
const CONTROL_TITLE = "Control title";
const CONTROL_DESCRIPTION = "Control description";
const RISK_COVERAGE = "Risk coverage (%)";
const RISK_ENTITY = "Entity";
const RISK = "Risk";
const SCOPE = "Scope";
const RISK_DESCRIPTION = "Risk Description";
const ASSESSED_STATUS = "Assessed";
const ASSESSED_DATE = "Last assessed";
const ASSESSED_USER = "Last assessed by";

interface NodeExporter {
    headerLabel: string;
    exportCallback: (node: DV.NodeDescriptor<T.MapNode>) => string | undefined;
}

interface ControlExporter {
    headerLabel: string;
    exportCallback: (
        control: ControlOverview,
        program: ControlProgramOverview,
        coverage: number | null | undefined
    ) => string | undefined;
}

type RiskExporter =
    | { type: "Node"; exporter: NodeExporter }
    | { type: "Control"; exporter: ControlExporter };

export interface RiskMatrixExportOptions {
    withAllUnassignedControls: boolean;
    asCSV: boolean;
}

/**
 * Generates an ExportableArray from the dataView (and list of extractor IDs) of a RiskMap.
 *
 * @param dataView The DataView of a RiskMap
 * @param extractorsIdRDLevel The list of IDs of extractors for Risk-Directory level Metadata (entity_level = false)
 * @param extractorsIdEntityLevel The list of IDs of extractors for Entity level Metadata (entity_level = true)
 * @param rootNode The root node view
 * @param allControls A reference to all controls & control programs
 */
export function exportRiskMatrix(
    dataView: DV.DataView<DV.NodeDescriptor<T.MapNode>>,
    extractorsIdRDLevel: Array<string>,
    extractorsIdEntityLevel: Array<string>,
    rootNode: RiskDirectoryNodeDto,
    allControls: ControlOverviewListDto,
    options: RiskMatrixExportOptions
): ExportableArray {
    const extractorRiskName = DV.getExtractor(
        dataView,
        RISK_EXTRACTOR_KEYS.NAME
    );
    const extractorRiskDescription = DV.getExtractor(
        dataView,
        RISK_EXTRACTOR_KEYS.DESCRIPTION
    );
    const extractorPath: DV.DataExtractor<
        DV.NodeDescriptor<T.MapNode>,
        string[]
    > = DV.getExtractor(dataView, RISK_EXTRACTOR_KEYS.PATH);
    const extractorsRDLevel = DV.getExtractors(dataView, extractorsIdRDLevel);
    const extractorEntity = DV.getExtractor(
        dataView,
        RISK_EXTRACTOR_KEYS.ENTITIES
    );
    const extractorScope = DV.getExtractor(dataView, RISK_EXTRACTOR_KEYS.SCOPE);
    const extractorsEntityLevel = DV.getExtractors(
        dataView,
        extractorsIdEntityLevel
    );
    const extractorsControl: DV.DataExtractor<
        DV.NodeDescriptor<T.MapNode>,
        MitigatingControl[]
    > = DV.getExtractor(dataView, RISK_EXTRACTOR_KEYS.CONTROLS);
    const extractorAssessmentStatus = DV.getExtractor(
        dataView,
        RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS
    );
    const extractorAssessmentUser = DV.getExtractor(
        dataView,
        RISK_EXTRACTOR_KEYS.ASSESSMENT_USER
    );
    const extractorAssessmentDate = DV.getExtractor(
        dataView,
        RISK_EXTRACTOR_KEYS.ASSESSMENT_DATE
    );

    const processFieldExporters: Array<NodeExporter> = [];
    const riskFieldExporters: Array<NodeExporter> = [
        {
            headerLabel: RISK,
            exportCallback: (node) =>
                sanitizeWhiteSpaces(extractorRiskName.asString(node)),
        },
        {
            headerLabel: SCOPE,
            exportCallback: (node) =>
                sanitizeWhiteSpaces(extractorScope.asString(node)),
        },
        {
            headerLabel: RISK_DESCRIPTION,
            exportCallback: (node) =>
                sanitizeWhiteSpaces(extractorRiskDescription.asString(node)),
        },
    ];
    const metadataRDLevelFieldExporters: Array<NodeExporter> = [];
    const entityFieldExporters: Array<NodeExporter> = [
        {
            headerLabel: RISK_ENTITY,
            exportCallback: (node) =>
                sanitizeWhiteSpaces(extractorEntity.asString(node)),
        },
    ];
    const metadataEntityLevelFieldExporters: Array<NodeExporter> = [];
    const controlProgramFieldExporters: Array<ControlExporter> = [
        {
            headerLabel: CONTROL_PROGRAM_NAME,
            exportCallback: (
                _control: ControlOverview,
                controlProgram: ControlProgramOverview
            ) => sanitizeWhiteSpaces(controlProgram.name),
        },
        {
            headerLabel: CONTROL_TITLE,
            exportCallback: (control: ControlOverview) =>
                sanitizeWhiteSpaces(control.title),
        },
        {
            headerLabel: CONTROL_DESCRIPTION,
            exportCallback: (control: ControlOverview) =>
                sanitizeWhiteSpaces(control.description ?? undefined),
        },
        {
            headerLabel: RISK_COVERAGE,
            exportCallback: (
                _control: ControlOverview,
                _controlProgram: ControlProgramOverview,
                riskCoverage: number | null | undefined
            ) => {
                let coverageNumber: number | undefined;
                switch (riskCoverage) {
                    case null:
                        // For ancient risk maps, back when it was possible not to set risk coverage
                        coverageNumber = 0;
                        break;
                    case undefined:
                        // Unassigned control (not linked to a risk), therefore the coverage cell should be blank
                        coverageNumber = undefined;
                        break;
                    default:
                        coverageNumber = riskCoverage;
                }
                return sanitizeWhiteSpaces(coverageNumber?.toString());
            },
        },
    ];

    const assessmentStatusFieldExporters: Array<NodeExporter> = [
        {
            headerLabel: ASSESSED_STATUS,
            exportCallback: (node) =>
                sanitizeWhiteSpaces(extractorAssessmentStatus.asString(node)),
        },
        {
            headerLabel: ASSESSED_DATE,
            exportCallback: (node) =>
                sanitizeWhiteSpaces(extractorAssessmentDate.asString(node)),
        },
        {
            headerLabel: ASSESSED_USER,
            exportCallback: (node) =>
                sanitizeWhiteSpaces(extractorAssessmentUser.asString(node)),
        },
    ];

    extractorsRDLevel.forEach((extractor) => {
        const { label, getValue } = extractor;
        metadataRDLevelFieldExporters.push({
            headerLabel: label,
            exportCallback: (node) => {
                const value: T.MetadataInstance = getValue(node);
                return exportMetadata(value);
            },
        });
    });

    extractorsEntityLevel.forEach((extractor) => {
        const { label, getValue } = extractor;
        metadataEntityLevelFieldExporters.push({
            headerLabel: label,
            exportCallback: (node) => {
                //to sanitize the comment
                if (getValue(node).type === "TextAreaInstance") {
                    return sanitizeWhiteSpaces(extractor.asString(node));
                }
                const value: T.MetadataInstance = getValue(node);
                return exportMetadata(value);
            },
        });
    });

    let maxProcessIndex = 1;
    DV.forEach(dataView.dataSource.iter(), (item) => {
        maxProcessIndex = Math.max(maxProcessIndex, item.ancestors.length - 1);
    });

    range(1, maxProcessIndex + 1).forEach((index) => {
        processFieldExporters.push({
            headerLabel: "Process" + index,
            exportCallback: (node) => {
                const path = extractorPath.getValue(node);
                return path ? path[index] : "";
            },
        });
        const descExtractor = forwardToRisk((ancestor) =>
            ancestor.type === "RiskDirectory"
                ? ""
                : ancestor.content.description
        );
        processFieldExporters.push({
            headerLabel: `Process${index} Description`,
            exportCallback: (node) =>
                node.ancestors[index] === undefined
                    ? ""
                    : descExtractor(node.ancestors[index]),
        });
    });

    // XXX: The risk map importer is closely tied to this column order
    const fieldExporters: Array<RiskExporter> = [
        ...processFieldExporters.map<RiskExporter>((exporter) => ({
            type: "Node",
            exporter,
        })),
        ...riskFieldExporters.map<RiskExporter>((exporter) => ({
            type: "Node",
            exporter,
        })),
        ...metadataRDLevelFieldExporters.map<RiskExporter>((exporter) => ({
            type: "Node",
            exporter,
        })),
        ...entityFieldExporters.map<RiskExporter>((exporter) => ({
            type: "Node",
            exporter,
        })),
        ...metadataEntityLevelFieldExporters.map<RiskExporter>((exporter) => ({
            type: "Node",
            exporter,
        })),
        ...controlProgramFieldExporters.map<RiskExporter>((exporter) => ({
            type: "Control",
            exporter,
        })),
        ...assessmentStatusFieldExporters.map<RiskExporter>((exporter) => ({
            type: "Node",
            exporter,
        })),
    ];

    const headerLabels = fieldExporters.map((e) => e.exporter.headerLabel);
    const content: Array<Array<string | undefined>> = [];

    // Set of controls that are assigned to at least one risk
    // (uses set for constant-time lookup)
    const associatedControlsId: Set<string> = new Set();

    DV.forEach(dataView.dataSource.iter(), (item) => {
        const node = item.value;
        if (node.type !== "ExpandedRiskNode") {
            return;
        }

        const controls = extractorsControl.getValue(item);
        if (controls !== undefined && controls.length !== 0) {
            controls.forEach((control) => {
                const record: Array<string | undefined> = [];
                fieldExporters.forEach((exporter: RiskExporter): void => {
                    switch (exporter.type) {
                        case "Node":
                            record.push(exporter.exporter.exportCallback(item));
                            break;
                        case "Control":
                            const { controlId, riskCoverage } = control;
                            associatedControlsId.add(controlId);
                            const controlDef =
                                rootNode.linkedResources.controls.find(
                                    (c) => c.id === controlId
                                );
                            const controlProgramDef =
                                rootNode.linkedResources.controlPrograms.find(
                                    (cp) =>
                                        cp.resource.id ===
                                        controlDef?.controlProgramId
                                );
                            if (
                                controlDef !== undefined &&
                                controlProgramDef !== undefined
                            ) {
                                record.push(
                                    exporter.exporter.exportCallback(
                                        controlDef,
                                        controlProgramDef.resource,
                                        riskCoverage
                                    )
                                );
                            }
                            break;
                        default:
                            return assertNever(exporter);
                    }
                });
                content.push(record);
            });
        } else {
            const record: Array<string | undefined> = [];
            fieldExporters.forEach((exporter) => {
                if (exporter.type === "Node") {
                    record.push(exporter.exporter.exportCallback(item));
                } else {
                    record.push(undefined);
                }
            });
            content.push(record);
        }
    });

    if (shouldIncludeAllUnassignedControls(options)) {
        // For all unassigned controls, we create a line in the export
        allControls.content
            .filter((control) => !associatedControlsId.has(control.resource.id))
            .forEach((control) => {
                const record: Array<string | undefined> = [];
                const controlProgram = allControls.linkedResources.find(
                    (cp) => cp.resource.id === control.resource.controlProgramId
                )?.resource;
                if (controlProgram !== undefined) {
                    fieldExporters.forEach((exporter) => {
                        if (exporter.type === "Control") {
                            record.push(
                                exporter.exporter.exportCallback(
                                    control.resource,
                                    controlProgram,
                                    undefined
                                )
                            );
                        } else {
                            record.push(undefined);
                        }
                    });
                }
                content.push(record);
            });
    }

    return {
        content,
        header: headerLabels,
    };
}

function shouldIncludeAllUnassignedControls(
    options: RiskMatrixExportOptions
): boolean {
    return options.withAllUnassignedControls;
}

function exportMetadata(metadata: T.MetadataInstance): string {
    switch (metadata.type) {
        case "CheckboxGroupInstance":
            return metadata.value
                ? exportArrayValue(metadata.value.map((val) => val.value))
                : "";
        case "DropdownInstance":
            return metadata.value?.value ?? "";
        case "HeadingInstance":
            return "";
        case "MetadataError":
            return "";
        case "NumericInputInstance":
            return metadata.value?.toString() ?? "";
        case "RadioButtonGroupInstance":
            return metadata.value?.value ?? "";
        case "TextAreaInstance":
            return metadata.value ?? "";
        case "TextInputInstance":
            return metadata.value ?? "";
        case "AttachedFilesInstance":
            return metadata.value
                ? exportArrayValue(metadata.value.map((val) => val.fileName))
                : "";
        default:
            return assertNever(metadata);
    }
}
